import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

/**
 * Hook to fetch lineup with player eligibility status
 */
// Stable JSON comparison to avoid unnecessary re-renders
const arePlayersEqual = (prev, next) => {
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    if (prev[i].id !== next[i].id) return false;
    if (prev[i].is_captain !== next[i].is_captain) return false;
    if (prev[i].is_starting !== next[i].is_starting) return false;
    const pe = prev[i].eligibility;
    const ne = next[i].eligibility;
    if (pe.eligible !== ne.eligible ||
        pe.isExpelled !== ne.isExpelled ||
        pe.isSubstituted !== ne.isSubstituted ||
        pe.yellowCards !== ne.yellowCards ||
        pe.expulsionReason !== ne.expulsionReason) return false;
  }
  return true;
};

export const useLineupWithStatus = (matchId) => {
  const [homeTeam, setHomeTeam] = useState([]);
  const [awayTeam, setAwayTeam] = useState([]);
  const [activeCounts, setActiveCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const prevHomeRef = useRef([]);
  const prevAwayRef = useRef([]);

  useEffect(() => {
    if (!matchId) return;
    
    console.log('[useLineupWithStatus] Starting fetch for matchId:', matchId);

    const isRefetch = refetchTrigger > 0;

    const fetchLineupData = async () => {
      try {
        // Only show loading on initial fetch, not on refetches (avoids unmount/remount)
        if (!isRefetch) setLoading(true);
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        const [lineupRes, countsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}/with-status`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/lineups/${matchId}/active-count`, { headers })
        ]);

        const players = lineupRes.data;
        
        // Separate by team and sort (active first, expelled last)
        const home = players
          .filter(p => p.team_id === countsRes.data.homeTeam.teamId)
          .sort((a, b) => {
            // Active first
            if (a.eligibility.eligible && !b.eligibility.eligible) return -1;
            if (!a.eligibility.eligible && b.eligibility.eligible) return 1;
            // Starters before subs
            if (a.is_starting && !b.is_starting) return -1;
            if (!a.is_starting && b.is_starting) return 1;
            return 0;
          });

        const away = players
          .filter(p => p.team_id === countsRes.data.awayTeam.teamId)
          .sort((a, b) => {
            if (a.eligibility.eligible && !b.eligibility.eligible) return -1;
            if (!a.eligibility.eligible && b.eligibility.eligible) return 1;
            if (a.is_starting && !b.is_starting) return -1;
            if (!a.is_starting && b.is_starting) return 1;
            return 0;
          });

        console.log('[useLineupWithStatus] Home players:', home.length);
        console.log('[useLineupWithStatus] Away players:', away.length);
        console.log('[useLineupWithStatus] Counts:', countsRes.data);

        // Only update state if data actually changed (prevents unnecessary re-renders)
        if (!arePlayersEqual(prevHomeRef.current, home)) {
          prevHomeRef.current = home;
          setHomeTeam(home);
        }
        if (!arePlayersEqual(prevAwayRef.current, away)) {
          prevAwayRef.current = away;
          setAwayTeam(away);
        }
        setActiveCounts(countsRes.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching lineup:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLineupData();
  }, [matchId, refetchTrigger]);

  return {
    homeTeam,
    awayTeam,
    activeCounts,
    loading,
    error,
    refetch: useCallback(() => setRefetchTrigger(prev => prev + 1), [])
  };
};
